import React, { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import { toast } from "react-toastify";
import { FiAlertTriangle, FiChevronRight, FiInfo, FiRotateCcw } from "react-icons/fi";
import {
  Section, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { HeaderLink, Notice, Tag } from "../../creditos/styles";
import { Capsule } from "../../creditos/components/ControlBar";
import { MoneyValue } from "../../creditos/components/parts";
import { formatCNPJ, formatPercent } from "../../creditos/dashboardData";
import { useAuth } from "../../../contexts/AuthContext";
import { hairline, surface, tokens as T } from "../../../styles/designTokens";
import { directionColor, signed } from "../dashboard/ReformaDashboard";
import { DEFAULT_PARAMS, PARAM_META, useReformaParams } from "../hooks/useReformaParams";
import { DraftNumber } from "../precos/PriceDashboard";
import { Control, RANGES, ThinNote } from "../simulacao/SimulationDashboard";
import { buildSimulation } from "../simulacao/simulationData";
import {
  PREMISES, currentRates, fixedRate, futureRates, rate, transitionRows,
} from "./parametrosData";
import { FluidRange, Num } from "../components/Motion";
import { finePercent } from "../components/Motion";

/**
 * Parâmetros: as premissas que movem o cálculo, as alíquotas de cada regime, o cronograma
 * legal e a classificação das empresas. Custos e margens por segmento ficam só na
 * calculadora de Preços, para o mesmo valor não ter dois lugares de edição.
 */

const FUTURE = IOS.green;
const label2 = ({ theme }) => tokens.label2(theme);
const label3 = ({ theme }) => tokens.label3(theme);
const dot = (color) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: 1 }} />;
const clamp = (v) => Math.max(0, Math.min(100, v));
// A API devolve alguns nomes como "RAZÃO SOCIAL:CNPJ"; o CNPJ já tem coluna própria
const companyName = (nome) => String(nome || "").replace(/:\d{14}$/, "");

const APPLIES = {
  yes: { color: IOS.green, label: "Aplica" },
  no: { color: IOS.gray, label: "Não aplica" },
  maybe: { color: IOS.orange, label: "Pode aplicar" },
};

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const PremiseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 32px;
  margin-top: 12px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const PremiseHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;

  .group { font-size: 0.72em; color: ${label3}; }
  .name { margin-top: 2px; font-size: 0.86em; font-weight: 500; letter-spacing: -0.012em; }
  .cell { flex-shrink: 0; display: flex; align-items: center; gap: 3px; width: 82px; height: 32px; padding: 0 9px; border-radius: 9px; background: ${({ theme }) => surface(theme, 2)}; font-size: 0.86em; }
  .cell:focus-within { box-shadow: 0 0 0 2px ${T.brandRing}; }
  .cell span { font-size: 0.86em; font-weight: 500; opacity: 0.5; }
  .cell input { flex: 1; width: 100%; min-width: 0; border: none; outline: none; background: transparent; color: ${({ theme }) => theme.text}; font: inherit; font-weight: 600; letter-spacing: -0.02em; text-align: right; font-variant-numeric: tabular-nums; -moz-appearance: textfield; }
  .cell input::-webkit-outer-spin-button, .cell input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .cell.changed input { color: ${T.brand}; }
`;

const PremiseFoot = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 2px;
  font-size: 0.72em;
  line-height: 1.35;
  color: ${label2};
  .ref { flex-shrink: 0; color: ${label3}; }
`;

const RateRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols};
  gap: 12px;
  align-items: center;
  min-height: 50px;
  padding: 6px 0;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  font-size: 0.84em;
  flex: 1 1 auto;

  &.head { flex: none; min-height: 0; padding: 0 0 7px; font-size: 0.72em; color: ${label3}; }
  &:last-child { border-bottom: none; }
  .name { min-width: 0; font-weight: 500; letter-spacing: -0.012em; }
  .name small { margin-left: 5px; font-size: 0.86em; font-weight: 300; color: ${label2}; }
  .rate { text-align: right; white-space: nowrap; font-weight: 600; letter-spacing: -0.025em; }
  .soft { min-width: 0; font-size: 0.94em; line-height: 1.35; color: ${label2}; }
`;

const Scroll = styled.div`
  flex: 1 0 auto;
  display: flex;
  margin-top: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
`;

const FUTURE_COLUMNS = "minmax(104px, 1fr) 58px minmax(92px, 1fr) 82px 84px";
const CURRENT_COLUMNS = "minmax(104px, 1fr) 58px minmax(96px, 0.9fr) minmax(130px, 1.4fr)";
const TRANSITION_COLUMNS = "minmax(130px, 170px) 58px 58px minmax(150px, 1fr) 62px minmax(110px, 150px) minmax(110px, 150px)";
const COMPANY_COLUMNS = "156px minmax(160px, 1fr) 62px 150px";

const TransitionTable = styled.div`
  flex: 1;
  min-width: 860px;
`;

const TransitionRow = styled.div`
  display: grid;
  grid-template-columns: ${TRANSITION_COLUMNS};
  gap: 14px;
  align-items: center;
  min-height: 44px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  font-size: 0.84em;

  &.head { min-height: 0; padding-bottom: 7px; font-size: 0.72em; color: ${label3}; }
  &:last-child { border-bottom: none; }
  .year { font-weight: 600; letter-spacing: -0.02em; }
  .year small { margin-left: 7px; font-size: 0.86em; font-weight: 300; letter-spacing: 0; color: ${label2}; }
  .n { text-align: right; white-space: nowrap; color: ${label2}; }
  .end { text-align: right; white-space: nowrap; font-weight: 600; letter-spacing: -0.025em; }
  .stack { display: flex; gap: 2px; height: 10px; }
  .stack i { display: block; min-width: 2px; }
  .stack i:first-child { border-radius: 4px 0 0 4px; }
  .stack i:last-child { border-radius: 0 4px 4px 0; }
  .old { display: grid; grid-template-columns: minmax(0, 1fr) 36px; gap: 8px; align-items: center; }
  .rail { height: 4px; border-radius: 2px; background: ${({ theme }) => surface(theme, 2)}; }
  .rail i { display: block; height: 100%; border-radius: 2px; background: ${label3}; }
  .old span { text-align: right; font-size: 0.92em; color: ${label2}; }
`;

const Facts = styled.dl`
  margin: 14px 0 0;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  font-size: 0.82em;
  dt, dd { margin: 0; padding: 9px 0; border-top: 1px solid ${({ theme }) => hairline(theme)}; }
  dt { padding-right: 14px; color: ${label2}; }
  dd { font-weight: 500; letter-spacing: -0.012em; text-align: right; line-height: 1.35; overflow-wrap: anywhere; }
  dd.none { font-weight: 400; color: ${label3}; }
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  width: 40px;
  height: 24px;
  cursor: ${({ $busy }) => ($busy ? "progress" : "pointer")};
  opacity: ${({ $busy }) => ($busy ? 0.6 : 1)};
  input { position: absolute; inset: 0; margin: 0; opacity: 0; cursor: inherit; z-index: 1; }
  span { position: absolute; inset: 0; border-radius: 999px; background: ${({ $on, theme }) => ($on ? T.brand : tokens.dk(theme) ? "rgba(120,120,128,0.32)" : "rgba(120,120,128,0.16)")}; transition: background 200ms ease; }
  span::after { content: ""; position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; border-radius: 50%; background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.2); transform: translateX(${({ $on }) => ($on ? "16px" : "0")}); transition: transform 200ms cubic-bezier(0.3, 1.4, 0.5, 1); }
  input:focus-visible + span { box-shadow: 0 0 0 3px ${T.brandRing}; }
  @media (prefers-reduced-motion: reduce) { span, span::after { transition: none; } }
`;

const CompanyCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  font-size: 0.92em;
  color: ${label2};
`;

function PremiseControl({ meta, value, onChange }) {
  const theme = useTheme();
  const range = RANGES[meta.id];
  const reference = DEFAULT_PARAMS[meta.id];
  const numeric = Number(value) || 0;
  const changed = numeric !== reference;
  const position = (v) => Math.max(0, Math.min(100, ((v - range.min) / (range.max - range.min)) * 100));
  return (
    <Control as="div" style={{ "--p": `${position(numeric)}%`, cursor: "default" }}>
      <PremiseHead>
        <div style={{ minWidth: 0 }}>
          <div className="group">{meta.group}</div>
          <div className="name">{meta.label}</div>
        </div>
        <label className={`cell${changed ? " changed" : ""}`}>
          <DraftNumber value={Math.round(numeric * 100) / 100} step={range.step} ariaLabel={`${meta.label}, em porcentagem`} onCommit={(v) => onChange(meta.id, clamp(v))} />
          <span>%</span>
        </label>
      </PremiseHead>
      <span className="slider" style={{ display: "block", "--fill": changed ? T.brand : tokens.label2(theme), "--rest": "rgba(118,118,128,0.28)" }}>
        <span className="mark" style={{ left: `${position(reference)}%` }} title={`Referência: ${formatPercent(reference)}`} />
        <FluidRange
          min={range.min}
          max={range.max}
          step={range.step}
          value={numeric}
          format={finePercent}
          aria-hidden="true"
          tabIndex={-1}
          onChange={(v) => onChange(meta.id, v)}
        />
      </span>
      <PremiseFoot>
        <span>{meta.caption}</span>
        <span className="ref">Referência <Num value={reference} format={formatPercent} /></span>
      </PremiseFoot>
    </Control>
  );
}

function FutureRatesCard({ params, isIndustrial }) {
  const rows = futureRates(params, isIndustrial);
  return (
    <Widget $span={2} $delay={100} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Regime futuro</div>
      <div className="w-sub">CBS e IBS SP seguem as premissas acima</div>
      <Scroll>
        <div style={{ flex: 1, minWidth: 460, display: "flex", flexDirection: "column" }}>
          <RateRow $cols={FUTURE_COLUMNS} className="head" aria-hidden="true">
            <span>Tributo</span><span style={{ textAlign: "right" }}>Alíquota</span><span>Base</span><span>Na empresa</span><span>Fonte</span>
          </RateRow>
          {rows.map((r) => (
            <RateRow key={r.name} $cols={FUTURE_COLUMNS}>
              <span className="name">{r.name}{r.note && <small>{r.note}</small>}</span>
              <span className="rate">{r.value}</span>
              <span className="soft">{r.base}</span>
              <span><Tag $color={APPLIES[r.applies].color}>{APPLIES[r.applies].label}</Tag></span>
              <span className="soft" style={{ whiteSpace: "nowrap" }}>{r.source}</span>
            </RateRow>
          ))}
        </div>
      </Scroll>
      <ThinNote>
        {isIndustrial
          ? "Há empresa marcada como indústria ou equiparada. O IS pode incidir na 1ª comercialização dos produtos listados: valide os NCMs das saídas contra a lista do IS antes de fechar a apuração."
          : "O IS incide só na saída do estabelecimento industrial ou importador, na 1ª comercialização, como o IPI hoje. Comércio e serviços não recolhem IS. Se existir nos produtos comprados, o custo vem embutido no preço de compra."}
      </ThinNote>
    </Widget>
  );
}

function CurrentRatesCard({ params }) {
  return (
    <Widget $span={2} $delay={150} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Regime atual, 2025</div>
      <div className="w-sub">Só o ISS é ajustável. As demais alíquotas são fixas no cálculo</div>
      <Scroll>
        <div style={{ flex: 1, minWidth: 460, display: "flex", flexDirection: "column" }}>
          <RateRow $cols={CURRENT_COLUMNS} className="head" aria-hidden="true">
            <span>Tributo</span><span style={{ textAlign: "right" }}>Alíquota</span><span>Regime</span><span>Observação</span>
          </RateRow>
          {currentRates(params).map((r) => (
            <RateRow key={r.name} $cols={CURRENT_COLUMNS}>
              <span className="name">{r.name}</span>
              <span className="rate">{r.value}</span>
              <span className="soft">{r.regime}</span>
              <span className="soft">{r.note}</span>
            </RateRow>
          ))}
        </div>
      </Scroll>
    </Widget>
  );
}

function TransitionCard({ params }) {
  const rows = transitionRows(params);
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <Widget $span={4} $delay={200} style={{ display: "flex", flexDirection: "column" }}>
      <TitleRow>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Tabela de transição</div>
          <div className="w-sub">LC 214/2025 e PLP 68/2024. O cronograma é legal e fixo; o último ano usa as premissas acima</div>
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: "0.76em" }}>
          <Tag $color={FUTURE}>CBS</Tag>
          <Tag $color={`${FUTURE}80`}>IBS SP</Tag>
        </div>
      </TitleRow>
      <Scroll>
        <TransitionTable>
        <TransitionRow className="head" aria-hidden="true">
          <span>Ano</span>
          <span style={{ textAlign: "right" }}>CBS</span>
          <span style={{ textAlign: "right" }}>IBS SP</span>
          <span>Alíquota nova</span>
          <span style={{ textAlign: "right" }}>Soma</span>
          <span>PIS/COFINS vigente</span>
          <span>ICMS/ISS vigente</span>
        </TransitionRow>
        {rows.map((r) => (
          <TransitionRow key={r.year} title={`${r.year}: CBS ${fixedRate(r.cbs)} + IBS SP ${fixedRate(r.ibs)} = ${fixedRate(r.total)}. PIS/COFINS ${rate(r.pisCofins)} e ICMS/ISS ${rate(r.icmsIss)} vigentes.`}>
            <span className="year">{r.year}<small>{r.phase}</small></span>
            <span className="n">{fixedRate(r.cbs)}</span>
            <span className="n">{fixedRate(r.ibs)}</span>
            <span className="stack" aria-hidden="true">
              <i style={{ width: `${(r.cbs / max) * 100}%`, background: FUTURE }} />
              <i style={{ width: `${(r.ibs / max) * 100}%`, background: FUTURE, opacity: 0.5 }} />
            </span>
            <span className="end">{fixedRate(r.total)}</span>
            <span className="old"><span className="rail" aria-hidden="true"><i style={{ width: `${r.pisCofins}%` }} /></span><span><Num value={r.pisCofins} format={rate} /></span></span>
            <span className="old"><span className="rail" aria-hidden="true"><i style={{ width: `${r.icmsIss}%` }} /></span><span><Num value={r.icmsIss} format={rate} /></span></span>
          </TransitionRow>
        ))}
        </TransitionTable>
      </Scroll>
    </Widget>
  );
}

function ClientCard({ project, empresas }) {
  const matriz = empresas.find((e) => e.tipo === "matriz");
  const branches = empresas.filter((e) => e.tipo !== "matriz").length;
  const facts = [
    ["Empresa", project?.nome || (matriz && companyName(matriz.nome))],
    ["CNPJ", project?.cnpj ? formatCNPJ(project.cnpj) : matriz && formatCNPJ(matriz.cnpj)],
    ["Regime", project?.regime],
    ["Filiais", project?.filiais || (empresas.length > 0 ? `${branches} ${branches === 1 ? "filial" : "filiais"}` : null)],
    ["Atividade", project?.atividade],
  ];
  return (
    <Widget $delay={250} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Dados do cliente</div>
      <div className="w-sub">Em Projetos, Editar, Dados Fiscais</div>
      <Facts>
        {facts.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt>{k}</dt>
            <dd className={v ? undefined : "none"}>{v || "Não informado"}</dd>
          </React.Fragment>
        ))}
      </Facts>
    </Widget>
  );
}

function CompaniesCard({ empresas, loading, error, onToggle, onRetry }) {
  const [saving, setSaving] = useState(null);

  const toggle = async (empresa) => {
    if (saving) return;
    setSaving(empresa.cnpj);
    try {
      await onToggle(empresa.cnpj, !empresa.atividadeIndustrial);
      toast.success(empresa.atividadeIndustrial ? `${companyName(empresa.nome)} desmarcada como indústria ou equiparada.` : `${companyName(empresa.nome)} marcada como indústria ou equiparada.`);
    } catch (e) {
      toast.error("Não foi possível salvar a classificação. Nada foi alterado.");
    } finally {
      setSaving(null);
    }
  };

  let body;
  if (loading) {
    body = <ThinNote>Carregando empresas…</ThinNote>;
  } else if (error) {
    body = (
      <Notice>
        <FiAlertTriangle />
        <span style={{ flex: 1 }}>Não foi possível carregar as empresas.</span>
        <Capsule type="button" onClick={onRetry} style={{ height: 28, padding: "0 10px" }}>Tentar novamente</Capsule>
      </Notice>
    );
  } else if (empresas.length === 0) {
    body = <ThinNote>Nenhuma empresa neste projeto ainda. Elas aparecem conforme os SPEDs são processados, os certificados são cadastrados ou as empresas são vinculadas no cadastro.</ThinNote>;
  } else {
    body = (
      <Scroll>
        <div style={{ flex: 1, minWidth: 460, display: "flex", flexDirection: "column" }}>
          <RateRow $cols={COMPANY_COLUMNS} className="head" aria-hidden="true">
            <span>CNPJ</span><span>Empresa</span><span>Tipo</span><span style={{ textAlign: "right" }}>Indústria ou equiparada</span>
          </RateRow>
          {empresas.map((e) => (
            <RateRow key={e.cnpj} $cols={COMPANY_COLUMNS}>
              <span className="soft" style={{ whiteSpace: "nowrap" }}>{formatCNPJ(e.cnpj)}</span>
              <span className="name">{companyName(e.nome)}</span>
              <span className="soft">{e.tipo === "matriz" ? "Matriz" : "Filial"}</span>
              <CompanyCell>
                {e.atividadeIndustrial ? "Sim" : "Não"}
                <Switch $on={e.atividadeIndustrial} $busy={saving === e.cnpj}>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={!!e.atividadeIndustrial}
                    disabled={saving === e.cnpj}
                    aria-label={`${companyName(e.nome)}, ${formatCNPJ(e.cnpj)}: indústria ou equiparada`}
                    onChange={() => toggle(e)}
                  />
                  <span />
                </Switch>
              </CompanyCell>
            </RateRow>
          ))}
        </div>
      </Scroll>
    );
  }

  return (
    <Widget $span={3} $delay={300} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Empresas, matriz e filiais</div>
      <div className="w-sub">Marque quem é indústria ou equiparada, para fins de IPI e Imposto Seletivo. A alteração é salva no projeto na hora</div>
      {body}
    </Widget>
  );
}

export default function ParametrosDashboard({
  fiscalData, empresas, loadingEmpresas, empresasError, onToggleIndustrial, onRetryEmpresas, isIndustrial, hasEcd, hasFilters, onGo,
}) {
  const { currentProject } = useAuth();
  const { params, setParam, resetParams } = useReformaParams();
  const simulation = useMemo(
    () => (fiscalData.totalMensal ? buildSimulation(fiscalData, params, DEFAULT_PARAMS, PARAM_META) : null),
    [fiscalData, params],
  );
  const impact = simulation?.impact;
  const changedCount = PREMISES.filter((m) => (Number(params[m.id]) || 0) !== DEFAULT_PARAMS[m.id]).length;
  const fullRate = (Number(params.cbsPlena2033) || 0) + (Number(params.ibsSpPlena2033) || 0);
  const marked = empresas.filter((e) => e.atividadeIndustrial).length;

  return (
    <>
      <Section $first>
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head">{dot(FUTURE)} Alíquota plena em 2033</div>
            <div className="w-value"><Num value={fullRate} format={formatPercent} /></div>
            <div className="w-foot">CBS <Num value={Number(params.cbsPlena2033) || 0} format={formatPercent} /> + IBS SP <Num value={Number(params.ibsSpPlena2033) || 0} format={formatPercent} /></div>
          </Widget>
          <Widget $delay={50}>
            <div className="w-head">{dot(directionColor(impact?.value || 0))} Impacto com estas premissas</div>
            <div className="w-value">{impact ? <MoneyValue value={impact.value} /> : "—"}</div>
            <div className="w-foot">
              {!impact && "Sem dados fiscais nestes filtros"}
              {impact && (changedCount > 0 ? <>Referência: <Num value={impact.reference} format={signed} /></> : `Futuro − Atual, ${hasFilters ? "no período filtrado" : "no período"}`)}
            </div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head">{dot(changedCount > 0 ? T.brand : IOS.gray)} Premissas alteradas</div>
            <div className="w-value">{changedCount} de {PREMISES.length}</div>
            <div className="w-foot">Valem até recarregar a página</div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head">{dot(isIndustrial ? IOS.orange : IOS.gray)} Indústria ou equiparada</div>
            <div className="w-value">{loadingEmpresas ? "—" : `${marked} de ${empresas.length}`}</div>
            <div className="w-foot">{isIndustrial ? "IS pode se aplicar" : "IS não se aplica"}</div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={60}>
        <Widget $delay={50}>
          <TitleRow>
            <div style={{ minWidth: 0 }}>
              <div className="w-title">Premissas do cálculo</div>
              <div className="w-sub">Digite o valor ou arraste. Todas as abas recalculam. O traço na régua marca a referência</div>
            </div>
            {changedCount > 0 && <Capsule type="button" onClick={resetParams} title="Voltar às premissas de referência" style={{ height: 28, padding: "0 10px" }}><FiRotateCcw /> Restaurar</Capsule>}
          </TitleRow>
          <PremiseGrid>
            {PREMISES.map((m) => <PremiseControl key={m.id} meta={m} value={params[m.id]} onChange={setParam} />)}
          </PremiseGrid>
          <Notice>
            <FiInfo />
            <span style={{ flex: 1 }}>Custo, frete, comissão, despesas e margem de cada segmento agora são editados na calculadora de Preços.</span>
            <HeaderLink type="button" onClick={() => onGo("formacao-preco")}>Abrir Preços <FiChevronRight /></HeaderLink>
          </Notice>
        </Widget>
      </Section>

      <Section $delay={100}>
        <WidgetGrid>
          <FutureRatesCard params={params} isIndustrial={isIndustrial} />
          <CurrentRatesCard params={params} />
          <TransitionCard params={params} />
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <WidgetGrid>
          <ClientCard project={currentProject} empresas={empresas} />
          <CompaniesCard empresas={empresas} loading={loadingEmpresas} error={empresasError} onToggle={onToggleIndustrial} onRetry={onRetryEmpresas} />
        </WidgetGrid>
        {hasEcd === false && (
          <Notice>
            <FiAlertTriangle />
            <span>
              <b style={{ fontWeight: 600 }}>Nenhuma ECD neste projeto.</b> A importação automática do plano de contas de despesa, no Cadastro de Preços, só funciona depois que ao menos um arquivo for processado. Envie na aba Upload o SPED Contábil (layout ECD) do exercício mais recente já encerrado. O sistema identifica o tipo pelo cabeçalho |0000|LECD|.
            </span>
          </Notice>
        )}
      </Section>
    </>
  );
}
